/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useCallback, useRef } from "react";
import { Stack } from "@mui/material";
import { SimplicityComposerEnsureTipTap } from "cms/rich-text/simplicity-composer-tiptap";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { SortableContent } from "components/sortable-content";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import LabelledField from "components/labelled-field";
import { ReactComponent as PlusRegularIcon } from "assets/images/icons/common/plus-regular-brand.svg";
import { CardEditor } from "./card";
import { AddLineWithIcon } from "components/add-line-with-icon";

const defaultCard = Object.freeze({});

const CardComponentEditor = () => {
    const { component, updateFactory } = useUpdateComponent();
    const onChangeContent = updateFactory("content");

    const allCards = component?.cards || [];
    const onChangeCards = updateFactory("cards");

    // Memoize the onChangeCards callback to prevent child re-renders
    const memoizedOnChangeCards = useCallback((cards) => {
        onChangeCards(cards);
    }, [onChangeCards]);

    const {
        list: cardsList,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        allCards,
        defaultCard,
        memoizedOnChangeCards
    );

    const {
        addNewItem: addNewCard,
        addNewItemBelow: addNewCardBelow,
        removeItem: removeCard,
        updateItems: updateCards
    } = controls;

    // Memoize updateCards to prevent child re-renders
    const memoizedUpdateCards = useCallback((cards) => {
        updateCards(cards);
    }, [updateCards]);

    // Keep a stable ref to the latest cards list so memoized child editors can always update from fresh state
    const cardsListRef = useRef(cardsList);
    cardsListRef.current = cardsList;

    return (
        <div className='simplicity-card-component-editor'>
            <div className='simplicity-card-component-editor-content'>
                <LabelledField label="Content">
                    <SimplicityComposerEnsureTipTap
                        richtext={component?.content}
                        onChange={onChangeContent}
                    />
                </LabelledField>
            </div>
            <div className='simplicity-card-component-editor-cards'>
                <div className='simplicity-card-component-editor-cards-header'>
                    <div className='simplicity-card-component-editor-cards-header-label'>
                        Cards
                    </div>
                    <div
                        className='simplicity-card-component-editor-cards-header-add'
                        onClick={addNewCard}
                    >
                        <div className='simplicity-card-component-editor-cards-header-add-icon'>
                            <PlusRegularIcon />
                        </div>
                        <div className='simplicity-card-component-editor-cards-header-add-text'>
                            Add
                        </div>
                    </div>
                </div>
                <div className='simplicity-card-component-editor-cards-list'>
                    <Stack direction="column">
                        {cardsList &&
                            cardsList.map((card, index) => {
                                const guid = card?.guid || `card-${index}`;
                                return (
                                    <>
                                        <SortableContent
                                            key={`banner-card-${guid}-${index}`}
                                            id={guid}
                                            index={index}
                                            sortableMethods={sortableMethods}
                                        >
                                            <CardEditor
                                                card={card}
                                                index={index}
                                                cardsList={cardsList}
                                                cardsListRef={cardsListRef}
                                                updateCards={memoizedUpdateCards}
                                            />
                                            <div>
                                                <PositionButtons
                                                    index={index}
                                                    list={cardsList}
                                                    handleUpdate={memoizedUpdateCards}
                                                />
                                                <RemoveIcon
                                                    className="simplicity-composer-field-editor-remove"
                                                    onDeleteItem={removeCard}
                                                    index={index}
                                                />
                                            </div>
                                        </SortableContent>
                                        <AddLineWithIcon
                                            index={index}
                                            addNewItem={addNewCardBelow}
                                        />
                                    </>
                                );
                            })}
                    </Stack>
                </div>
            </div>
        </div>
    );
};

CardComponentEditor.displayName = "CardComponentEditor";
export { CardComponentEditor };
